import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../hooks/redux';
import { usePermissions } from '../../hooks/usePermissions';
import { updateBulkDraft, saveDraft } from '../../store/slices/googleReviewSlice';
import type { GoogleReviewView } from '../../types/googleReview';
import { dateFormatted } from '../../helpers/dateFormatted';

interface BulkReviewCardProps {
  review: GoogleReviewView;
  draftText: string;
}

const MAX_CHARS = 4096;

const StarRating: React.FC<{ rating: number }> = ({ rating }) => (
  <span className="bulk-review-card__stars" aria-label={`${rating} out of 5 stars`}>
    {Array.from({ length: 5 }, (_, i) => (
      <span key={i} className={i < rating ? 'review-card__star--filled' : 'review-card__star--empty'}>
        ★
      </span>
    ))}
  </span>
);

const BulkReviewCard: React.FC<BulkReviewCardProps> = ({ review, draftText }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  // Subuser: saving drafts requires google_review_write.
  const { can } = usePermissions();
  const canWrite = can('google_review_write');
  const reviewId = review.reviewId ?? '';

  const charCount = draftText.length;
  const isOverLimit = charCount > MAX_CHARS;

  const handleSaveToDraft = () => {
    dispatch(saveDraft({ reviewId, text: draftText }));
  };

  return (
    <div className="bulk-review-card">
      {/* Left: review details */}
      <div className="bulk-review-card__review">
        <div className="bulk-review-card__review-header">
          <div className="review-card__avatar" aria-hidden="true">
            {review.reviewerAvatarInitials}
          </div>
          <div className="review-card__meta">
            <span className="review-card__name">{review.reviewerName}</span>
            <span className="review-card__date">{dateFormatted(review.createdAt ?? '')}</span>
          </div>
        </div>
        <StarRating rating={review.rating ?? 0} />
        <p className="bulk-review-card__review-text">{review.text}</p>
      </div>

      {/* Right: editable draft textarea */}
      <div className="bulk-review-card__reply">
        <label className="bulk-review-card__reply-label">
          {t('google_review_bulk_reply_label')}
        </label>
        <textarea
          className={`bulk-review-card__textarea${isOverLimit ? ' bulk-review-card__textarea--over-limit' : ''}`}
          value={draftText}
          onChange={(e) =>
            dispatch(updateBulkDraft({ reviewId, text: e.target.value }))
          }
          rows={6}
        />
        <div className="bulk-review-card__footer">
          <span className={`bulk-review-card__char-counter${isOverLimit ? ' bulk-review-card__char-counter--over' : ''}`}>
            {charCount} / {MAX_CHARS}
          </span>
          <button
            className="bulk-review-card__save-link"
            onClick={handleSaveToDraft}
            disabled={charCount === 0 || isOverLimit || !canWrite}
            title={!canWrite ? t('subuser_no_permission_tooltip') : undefined}
          >
            {t('google_review_bulk_save_draft')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkReviewCard;
